import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Phone, Mail, MapPin, Loader, Calendar, Check, User, FileText, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function OfficeDetails() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const contactInfo = {
    phone: "+1 (519) 373-4046",
    email: "joe@joewestinspections.com",
    address: "Port Elgin, ON, N0H2C1"
  };
  return <section id="office-details" ref={ref} className="bg-background overflow-hidden ![padding:50px_0px_128px_0px]">
      <div className="container mx-auto px-6">
        <div className={cn("grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="flex flex-col justify-center space-y-8">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground font-sans">My Digital Headquarters&nbsp;</h2>
              <p className="text-[18px] leading-[1.6] text-foreground/80 max-w-lg">Call, Text, Email or Fill Out The Form Below and I Will Call You Back.</p>
            </div>

            <div className="space-y-5">
              <Link to={`tel:${contactInfo.phone.replace(/\D/g, "")}`} className="group flex items-center gap-4 text-lg font-medium text-foreground hover:text-secondary transition-colors w-fit">
                <span className="flex items-center justify-center size-12 rounded-full bg-secondary/10 group-hover:bg-secondary/20 transition-colors">
                  <Phone className="size-6 text-secondary stroke-[2px]" />
                </span>
                <span>+1 (519) 373-4046</span>
              </Link>

              <Link to={`mailto:${contactInfo.email}`} className="group flex items-center gap-4 text-lg font-medium text-foreground hover:text-secondary transition-colors w-fit">
                <span className="flex items-center justify-center size-12 rounded-full bg-secondary/10 group-hover:bg-secondary/20 transition-colors">
                  <Mail className="size-6 text-secondary stroke-[2px]" />
                </span>
                <span>joe@joewestinspections.com</span>
              </Link>

              <div className="flex items-center gap-4 text-lg font-medium text-foreground w-fit">
                <span className="flex items-center justify-center size-12 rounded-full bg-secondary/10">
                  <MapPin className="size-6 text-secondary stroke-[2px]" />
                </span>
                <span>Port Elgin, ON, N0H2C1</span>
              </div>
            </div>
          </div>

          <div className="relative w-full h-full min-h-[400px] lg:min-h-[500px]">
            <div className="bg-black p-8 rounded-xl shadow-lg border border-border/50 text-white">
              <FormProvider formId="request_inspection_form_inspections" sectionName="office-details" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              property_address: "",
              inspection_type: "",
              preferred_date: "",
              additional_details: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              property_address: {
                required: true
              },
              inspection_type: {
                required: true
              },
              preferred_date: {
                required: true
              },
              additional_details: {
                required: false
              }
            }} submitText="Submit Request" successMessage="Your inspection request has been received! We will contact you shortly to confirm. Please check your Junk Email Folder for my response." errorMessage="There was a problem submitting your request. Please try again.">
                <InspectionForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function InspectionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-primary/20 p-4 rounded-full text-primary">
          <Check className="size-12" />
        </div>
        <h3 className="text-2xl font-bold text-foreground font-sans">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Jane Doe" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="email" placeholder="jane@example.com" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="(555) 123-4567" className="pl-9" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="property_address" rules={{
        required: "Property Address is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel>Property Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 size-4 text-muted-foreground" />
                  <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 pl-9 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30" placeholder="123 Main St, Port Elgin, ON" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inspection_type" rules={{
        required: "Please select an inspection type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Product Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Residential Property Inspection">
                    Residential Property Inspection
                  </SelectItem>
                  <SelectItem value="Commercial Property Inspection">
                    Commercial Property Inspection
                  </SelectItem>
                  <SelectItem value="WETT Inspection">
                    WETT Inspection
                  </SelectItem>
                  <SelectItem value="Septic System Inspection">
                    Septic System Inspection
                  </SelectItem>
                  <SelectItem value="Architectural Drawings">
                    Architectural Drawings
                  </SelectItem>
                  <SelectItem value="Construction Consultation">
                    Construction Consultation 
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "Preferred Date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-muted-foreground" /></SafeHydrate>
                  <Input type="date" className="pl-9 block w-full" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel>Additional Details</FormLabel>
              <FormControl>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 size-4 text-muted-foreground" />
                  <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 pl-9 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30" placeholder="Any special requirements or questions?" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <Info className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-lg font-semibold shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting...
          </> : "Submit Request"}
      </Button>
    </Form>;
}